export type AdminPaymentDisplayContext = 'subscription' | 'order' | 'summary';

export type AdminPaymentDisplayBadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning';

export type AdminPaymentDisplayStatusInput = {
  status: string | null | undefined;
  failReason?: string | null;
  lastPaymentResult?: string | null;
  orderFailReason?: string | null;
  failureCategory?: string | null;
  context?: AdminPaymentDisplayContext;
};

export type AdminPaymentDisplayStatus = {
  label: string;
  variant: AdminPaymentDisplayBadgeVariant;
  detail?: string;
};

const STALE_PENDING_TIMEOUT_REASON = 'stale_pending_timeout';

const FAILURE_CATEGORY_LABELS: Record<string, string> = {
  renewal_payment_failure: '갱신 결제 실패',
  billing_payment_failure: '결제/빌링 실패',
  registration_failure: '등록 실패',
  user_cancel: '사용자 취소',
  admin_cancel: '관리자 취소',
  order_payment_failed: '주문 결제 실패',
  checkout_timeout: '결제 미완료(시간 초과)',
  order_cancelled: '주문 취소',
  order_failed: '주문 실패',
};

const DETAIL_CATEGORIES = new Set([
  'order_payment_failed',
  'renewal_payment_failure',
  'billing_payment_failure',
  'registration_failure',
  'checkout_timeout',
  'order_failed',
]);

const normalizeStatus = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.toLowerCase() : null;
};

const normalizeCategory = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed || null;
};

const maskPanLikeSequences = (text: string): string => {
  return text.replace(/\d(?:[\s-]*\d){12,18}/g, (match) => {
    const digits = match.replace(/\D/g, '');
    return digits.length >= 13 && digits.length <= 19 ? '****' : match;
  });
};

/** Redact card-like sequences and billing-key payloads before showing in admin UI. */
export const sanitizeAdminPaymentDetail = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  let sanitized = value.trim();
  if (!sanitized) return undefined;

  sanitized = maskPanLikeSequences(sanitized);
  // Only redact when a delimiter follows "billing key" / "BID" (avoids "registration" after "key ")
  sanitized = sanitized.replace(/billing\s*key\s*[:=]\s*[\w-]{8,}/gi, 'billing key: ****');
  sanitized = sanitized.replace(/\bBID\s*[:=]\s*[\w-]{8,}/gi, 'BID: ****');

  return sanitized;
};

const truncateDetail = (value: string | null | undefined, maxLength = 120): string | undefined => {
  const sanitized = sanitizeAdminPaymentDetail(value);
  if (!sanitized) return undefined;
  return sanitized.length <= maxLength ? sanitized : `${sanitized.slice(0, maxLength - 1)}…`;
};

const pickDetailForContext = (
  context: AdminPaymentDisplayContext | undefined,
  ...values: Array<string | null | undefined>
): string | undefined => {
  const ordered = context === 'order'
    ? values
    : context === 'subscription'
      ? [values[1], values[2], values[0]]
      : values;

  for (const value of ordered) {
    const detail = truncateDetail(value);
    if (detail) return detail;
  }
  return undefined;
};

/** Client fallback when backend failureCategory is absent (e.g. legacy payloads). */
const resolveFailureCategoryFallback = (input: AdminPaymentDisplayStatusInput): string | null => {
  const status = normalizeStatus(input.status);
  if (!status) return null;

  if (status === 'registration_failure') return 'registration_failure';
  if (status === 'canceled_by_user') return 'user_cancel';
  if (status === 'canceled_by_admin') return 'admin_cancel';

  if (status === 'failed') {
    return input.failReason?.trim() ? 'order_payment_failed' : 'order_failed';
  }

  if (status === 'cancelled' || status === 'canceled') {
    return normalizeCategory(input.failReason) === STALE_PENDING_TIMEOUT_REASON
      ? 'checkout_timeout'
      : 'order_cancelled';
  }

  return null;
};

const resolveVariant = (
  status: string | null,
  category: string | null,
): AdminPaymentDisplayBadgeVariant => {
  if (category && FAILURE_CATEGORY_LABELS[category]) {
    switch (category) {
      case 'renewal_payment_failure':
      case 'billing_payment_failure':
      case 'registration_failure':
      case 'order_payment_failed':
      case 'order_failed':
        return 'destructive';
      case 'checkout_timeout':
        return 'warning';
      case 'user_cancel':
      case 'admin_cancel':
      case 'order_cancelled':
        return 'secondary';
      default:
        return 'outline';
    }
  }

  if (category) {
    return 'outline';
  }

  if (!status) return 'secondary';

  switch (status) {
    case 'active':
    case 'completed':
    case 'paid':
      return 'success';
    case 'on_hold':
    case 'in_grace_period':
    case 'grace_period':
    case 'paused':
    case 'pending':
    case 'processing':
      return 'warning';
    case 'failed':
    case 'payment_failure':
    case 'registration_failure':
    case 'revoked':
      return 'destructive';
    case 'cancelled':
    case 'canceled':
    case 'canceled_by_admin':
    case 'canceled_by_user':
    case 'expired':
    case 'refunded':
    case 'canceled_until_expiry':
    case 'free':
      return 'secondary';
    default:
      return 'outline';
  }
};

const resolveDefaultLabel = (status: string | null): string => {
  if (!status) return '상태 없음';

  switch (status) {
    case 'active':
      return '활성';
    case 'cancelled':
    case 'canceled':
      return '취소';
    case 'canceled_by_admin':
      return '관리자 취소';
    case 'canceled_by_user':
      return '사용자 취소';
    case 'expired':
      return '만료';
    case 'on_hold':
      return '보류';
    case 'in_grace_period':
    case 'grace_period':
      return '결제 유예';
    case 'paused':
      return '일시중지';
    case 'pending':
      return '대기';
    case 'approved':
      return '승인';
    case 'processing':
      return '처리 중';
    case 'completed':
      return '완료';
    case 'paid':
      return '결제 완료';
    case 'failed':
      return '주문 실패';
    case 'payment_failure':
      return '결제 실패';
    case 'registration_failure':
      return '등록 실패';
    case 'refunded':
      return '환불';
    case 'revoked':
      return '권한 회수';
    case 'canceled_until_expiry':
      return '해지 예정';
    case 'free':
      return '무료';
    case 'rejected':
      return '거절';
    case 'processed':
      return '처리 완료';
    case 'superseded':
      return '교체됨';
    default:
      return status.toUpperCase();
  }
};

export const resolveAdminPaymentDisplayStatus = (
  input: AdminPaymentDisplayStatusInput,
): AdminPaymentDisplayStatus => {
  const normalizedStatus = normalizeStatus(input.status);
  const category = normalizeCategory(input.failureCategory) ?? resolveFailureCategoryFallback(input);
  const label = category && FAILURE_CATEGORY_LABELS[category]
    ? FAILURE_CATEGORY_LABELS[category]
    : category
      ? category
      : resolveDefaultLabel(normalizedStatus);

  const detail = category && DETAIL_CATEGORIES.has(category)
    ? pickDetailForContext(
        input.context,
        input.failReason,
        input.lastPaymentResult,
        input.orderFailReason,
      )
    : undefined;

  return {
    label,
    variant: resolveVariant(normalizedStatus, category),
    detail,
  };
};

/** @deprecated Prefer resolveAdminPaymentDisplayStatus for payment/subscription badges. */
export const formatSubscriptionStatus = (
  value: string | null | undefined,
  options?: Omit<AdminPaymentDisplayStatusInput, 'status'>,
): string => {
  return resolveAdminPaymentDisplayStatus({ status: value, context: 'summary', ...options }).label;
};

export const getStatusBadgeVariant = (
  value: string | null | undefined,
  options?: Omit<AdminPaymentDisplayStatusInput, 'status'>,
): AdminPaymentDisplayBadgeVariant => {
  return resolveAdminPaymentDisplayStatus({ status: value, ...options }).variant;
};
